/**
 * RoundFormBase
 *
 * Outer shell shared by per-game-type round create/edit forms. Owns:
 *   - the `round-creation-content` container
 *   - the empty-players guard (no enabled players → message + Cancel only)
 *   - the submit/cancel actions row with consistent labels
 *
 * Game-specific form bodies render as children; the submit button is driven
 * by the parent (so it can also disable based on form-specific validity).
 */

import { ReactNode } from 'react';

export interface RoundFormBaseProps {
  playerCount: number;
  isEditing?: boolean;
  /** Disable the submit button (e.g. when game-specific validation fails). */
  submitDisabled?: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  /** Optional extra utility class on the outer container (e.g. `fpr-creation`). */
  extraClassName?: string;
  /**
   * Whether the round needs players before it can be created or edited
   * (default true). Hide and Seek Teams sets its rounds up before anyone joins.
   */
  requiresPlayers?: boolean;
  /** The submit button's label, when it does not create or save a round. */
  submitLabel?: string;
  children: ReactNode;
}

export function RoundFormBase({
  playerCount,
  isEditing = false,
  submitDisabled = false,
  onSubmit,
  onCancel,
  extraClassName,
  requiresPlayers = true,
  submitLabel,
  children,
}: RoundFormBaseProps) {
  const containerClass = `round-creation-content${extraClassName ? ` ${extraClassName}` : ''}`;

  if (requiresPlayers && playerCount === 0) {
    return (
      <div className={containerClass}>
        <p className="text-red fw-medium">
          No enabled players in game. Players must join before {isEditing ? 'editing' : 'creating'} rounds.
        </p>
        <div className="round-creation-actions">
          <button onClick={onCancel} className="btn btn--outline btn--slate">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {children}

      <div className="round-creation-actions">
        <button
          onClick={onSubmit}
          className="btn btn--green"
          disabled={submitDisabled}
        >
          {submitLabel ?? (isEditing ? '💾 Save Changes' : '✅ Create Round')}
        </button>
        <button onClick={onCancel} className="btn btn--outline btn--slate">
          Cancel
        </button>
      </div>
    </div>
  );
}
